import {
  Typography,
  Box,
  Select,
  MenuItem,
  IconButton,
  TableCell,
  Table,
  TableBody,
  TableRow,
  TableHead,
  TableContainer,
  Grid,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { Proposal, Job, Organization, SaveQuickbooksIntegrationInput } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import FormField from "components/FormField/FormField";
import { StyledTableRow } from "modules/jobs/JobsSchedules/JobsSchedules";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import DeleteIcon from "@mui/icons-material/Delete";
import { resolveProposalProductDescription } from "./quickbooksSyncUtils";

export default function QuickbooksEditableInformation({
  proposalId,
  job,
  organization,
  onChange,
  externalId,
  isInvoice = false,
  hideItemsSection = false,
}: {
  proposalId: Proposal["id"];
  job: Job;
  organization: Organization;
  onChange: (data: SaveQuickbooksIntegrationInput) => void;
  externalId: string;
  isInvoice?: boolean;
  hideItemsSection?: boolean;
}) {
  const totalItemsAmount =
    job?.jobPhases.reduce(
      (sum, phase) =>
        sum +
        (typeof phase?.proposedOverallTotal === "number"
          ? phase.proposedOverallTotal
          : parseFloat(phase.proposedOverallTotal || "0")),
      0
    ) || 0;

  const totalTaxPercentage =
    job?.proposal?.salesTaxes?.reduce(
      (sum, tax) =>
        sum +
        (typeof tax.percentage === "string"
          ? parseFloat(tax.percentage)
          : Number(tax.percentage) || 0),
      0
    ) || 0;

  const calculatedTaxAmount = Math.round(totalItemsAmount * (totalTaxPercentage / 100));

  const calculatedTotalAfterTaxes = totalItemsAmount + calculatedTaxAmount;

  const initialFormDataInformation = useMemo(
    () =>
      ({
        customerName: job?.company?.name || "",
        customerPhone: job?.company?.phone || "",
        customerEmail: job?.company?.email || "",
        customerBillingAddressLine1: job?.company?.addressLine1 || "",
        customerBillingAddressLine2: job?.company?.addressLine2 || "",
        customerBillingAddressZip: job?.company?.addressZip || "",
        customerBillingAddressCity: job?.company?.addressCity || "",
        customerBillingAddressState: job?.company?.addressState || "",
        customerBillingAddressCountry: job?.company?.addressCountry || "",
        invoiceNumber: externalId || `${proposalId}-${job?.id ?? ""}` || "",
        taxReference: "",
        jobName: job?.proposalStage?.name || "",
        shipAddressAddressLine1: job?.address?.line1 || "",
        shipAddressAddressLine2: job?.address?.line2 || "",
        shipAddressAddressZip: job?.address?.zip || "",
        shipAddressAddressCity: job?.address?.city || "",
        shipAddressAddressState: job?.address?.state || "",
        items: job?.jobPhases.map((phase) => ({
          itemsName:
            phase?.proposalProduct?.material?.externalProductName ??
            phase?.proposalProduct?.material?.name ??
            "",
          itemsDescription: resolveProposalProductDescription(
            phase,
            `Job ID: ${job?.id}, Job Phase ID: ${phase?.id}, Job Name: ${job?.proposalStage?.name}`
          ),
          itemAmount:
            typeof phase?.proposedOverallTotal === "number"
              ? phase.proposedOverallTotal
              : parseFloat(phase.proposedOverallTotal || "0"),
        })),
        totalAmountItems: totalItemsAmount,
        totalAmountTaxes: calculatedTaxAmount,
        totalAmountAfterTaxes: calculatedTotalAfterTaxes,
      }) as SaveQuickbooksIntegrationInput & {
        totalAmountTaxes?: string | number;
        totalAmountAfterTaxes?: string | number;
      },
    [
      calculatedTaxAmount,
      calculatedTotalAfterTaxes,
      externalId,
      job,
      proposalId,
      totalItemsAmount,
    ]
  );

  const [formFieldsData, setFormFieldsData] = useState<
    SaveQuickbooksIntegrationInput & {
      totalAmountTaxes?: string | number;
      totalAmountAfterTaxes?: string | number;
    }
  >(initialFormDataInformation);
  const salesTaxes = organization?.salesTaxes;

  const [taxManuallyEdited, setTaxManuallyEdited] = useState(false);

  useEffect(() => {
    setFormFieldsData(initialFormDataInformation);
    setTaxManuallyEdited(false);
  }, [initialFormDataInformation]);

  useEffect(() => {
    onChange(formFieldsData);
  }, [formFieldsData, onChange]);

  const calculateTotalAmountItems = (items) => {
    const itemsTotal = items.reduce(
      (sum, item) => sum + parseFloat(item.itemAmount.toString() || "0"),
      0
    );
    return itemsTotal;
  };

  const calculateTaxAndTotal = (itemsTotal) => {
    const taxAmount = Math.round(itemsTotal * (totalTaxPercentage / 100));
    const totalAfterTaxes = itemsTotal + taxAmount;
    return { taxAmount, totalAfterTaxes };
  };

  const handleFieldDataChange = (key: string, value: string, index?: number) => {
    let newValue = value;
    if (
      (key == "items.itemAmount" || key == "totalAmountTaxes" || key == "totalAmountAfterTaxes") &&
      value
    ) {
      newValue = String(parseFloat(value) * 100);
    }

    if (key === "totalAmountTaxes") {
      setTaxManuallyEdited(!!value);
    }

    setFormFieldsData((prevState) => {
      if (index !== undefined && key.startsWith("items.")) {
        const field = key.split(".")[1];
        const newItems = [...prevState.items];
        newItems[index] = {
          ...newItems[index],
          [field]: newValue.length ? newValue : initialFormDataInformation["items"][index][field],
        };
        const total = calculateTotalAmountItems(newItems);

        const taxAmount = taxManuallyEdited
          ? prevState.totalAmountTaxes
          : calculateTaxAndTotal(total).taxAmount;
        const totalAfterTaxes = total + Number(taxAmount);

        return {
          ...prevState,
          items: newItems,
          totalAmountItems: Number(total),
          totalAmountTaxes: taxAmount,
          totalAmountAfterTaxes: totalAfterTaxes,
        };
      } else if (key === "totalAmountItems" && newValue) {
        const itemsTotal = parseFloat(newValue);

        const taxAmount = taxManuallyEdited
          ? prevState.totalAmountTaxes
          : calculateTaxAndTotal(itemsTotal).taxAmount;
        const totalAfterTaxes = itemsTotal + Number(taxAmount);

        return {
          ...prevState,
          [key]: Number(newValue || initialFormDataInformation[key]),
          totalAmountTaxes: taxAmount,
          totalAmountAfterTaxes: totalAfterTaxes,
        };
      } else if (key === "totalAmountTaxes") {
        if (newValue) {
          const taxAmount = Number(newValue);
          return {
            ...prevState,
            totalAmountTaxes: taxAmount,
            totalAmountAfterTaxes: prevState.totalAmountItems + taxAmount,
          };
        } else {
          const { taxAmount, totalAfterTaxes } = calculateTaxAndTotal(prevState.totalAmountItems);
          return {
            ...prevState,
            totalAmountTaxes: taxAmount,
            totalAmountAfterTaxes: totalAfterTaxes,
          };
        }
      } else {
        return {
          ...prevState,
          [key]: newValue || initialFormDataInformation[key],
        };
      }
    });
  };

  const handleRemove = (index: number) => {
    setFormFieldsData((prevState) => {
      const newItems = prevState.items.filter((_, i) => i !== index);
      const total = calculateTotalAmountItems(newItems);
      const { taxAmount, totalAfterTaxes } = calculateTaxAndTotal(total);
      initialFormDataInformation.items = newItems;
      return {
        ...prevState,
        items: newItems,
        totalAmountItems: total,
        totalAmountTaxes: taxAmount,
        totalAmountAfterTaxes: totalAfterTaxes,
      };
    });
  };

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={12} lg={6}>
          <TableContainer sx={{ minWidth: 0, overflowX: "auto" }}>
            <Table
              aria-label="customer information table"
              size="small"
              sx={{
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  px: 0.75,
                  py: 0.35,
                  fontSize: "0.75rem",
                  lineHeight: 1.25,
                },
                "& .MuiInputBase-root": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputBase-input": {
                  py: 0.35,
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  py: 0.6,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                },
              }}
            >
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow sx={{ backgroundColor: "grey.300" }}>
                  <TableCell>Customer Information</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <StyledTableRow className={"outer even"}>
                  <TableCell sx={{ width: "31%" }}>Customer Name</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.company?.name}
                      onChange={(e) => handleFieldDataChange("customerName", e.target.value)}
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer odd"}>
                  <TableCell sx={{ width: "31%" }}>Customer Phone</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.company?.phone}
                      onChange={(e) => handleFieldDataChange("customerPhone", e.target.value)}
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer even"}>
                  <TableCell sx={{ width: "31%" }}>Customer Email</TableCell>
                  <TableCell>
                    <FormField
                      type="email"
                      placeholder={job?.company?.email}
                      onChange={(e) => handleFieldDataChange("customerEmail", e.target.value)}
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer odd"}>
                  <TableCell sx={{ width: "31%" }}>Address Line 1</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.company?.addressLine1}
                      onChange={(e) =>
                        handleFieldDataChange("customerBillingAddressLine1", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer even"}>
                  <TableCell sx={{ width: "31%" }}>Address Line 2</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.company?.addressLine2}
                      onChange={(e) =>
                        handleFieldDataChange("customerBillingAddressLine2", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer odd"}>
                  <TableCell sx={{ width: "31%" }}>City</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.company?.addressCity}
                      onChange={(e) =>
                        handleFieldDataChange("customerBillingAddressCity", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer even"}>
                  <TableCell sx={{ width: "31%" }}>State</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.company?.addressState}
                      onChange={(e) =>
                        handleFieldDataChange("customerBillingAddressState", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer odd"}>
                  <TableCell sx={{ width: "31%" }}>Zip</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.company?.addressZip}
                      onChange={(e) =>
                        handleFieldDataChange("customerBillingAddressZip", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} lg={6}>
          <TableContainer sx={{ minWidth: 0, overflowX: "auto" }}>
            <Table
              aria-label="invoice information table"
              size="small"
              sx={{
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  px: 0.75,
                  py: 0.35,
                  fontSize: "0.75rem",
                  lineHeight: 1.25,
                },
                "& .MuiInputBase-root": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputBase-input": {
                  py: 0.35,
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  py: 0.6,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                },
              }}
            >
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow sx={{ backgroundColor: "grey.300" }}>
                  <TableCell>Invoice Information</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <StyledTableRow className={"outer even"}>
                  <TableCell sx={{ width: "31%" }}>Invoice Number</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={externalId}
                      onChange={(e) => handleFieldDataChange("invoiceNumber", e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer odd"}>
                  <TableCell sx={{ width: "31%" }}>Job Name</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.proposalStage?.name}
                      onChange={(e) => handleFieldDataChange("jobName", e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer even"}>
                  <TableCell sx={{ width: "31%" }}>Ship Address 1</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.address?.line1}
                      onChange={(e) =>
                        handleFieldDataChange("shipAddressAddressLine1", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer odd"}>
                  <TableCell sx={{ width: "31%" }}>Ship Address 2</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.address?.line2}
                      onChange={(e) =>
                        handleFieldDataChange("shipAddressAddressLine2", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer even"}>
                  <TableCell sx={{ width: "31%" }}>Ship City</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.address?.city}
                      onChange={(e) =>
                        handleFieldDataChange("shipAddressAddressCity", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer odd"}>
                  <TableCell sx={{ width: "31%" }}>Ship State</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.address?.state}
                      onChange={(e) =>
                        handleFieldDataChange("shipAddressAddressState", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow className={"outer even"}>
                  <TableCell sx={{ width: "31%" }}>Ship Zip</TableCell>
                  <TableCell>
                    <FormField
                      placeholder={job?.address?.zip}
                      onChange={(e) =>
                        handleFieldDataChange("shipAddressAddressZip", e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </StyledTableRow>
                {!isInvoice && (
                  <StyledTableRow className={"outer odd"}>
                    <TableCell sx={{ width: "31%" }}>Tax Reference</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        {formFieldsData.taxReference === "" && (
                          <InputLabel
                            id="sales-taxes-select"
                            style={{ marginLeft: "-1em", color: "#b3b3b3" }}
                          >
                            Please select
                          </InputLabel>
                        )}
                        <Select
                          id="sales-taxes-select"
                          labelId="sales-taxes-select-label"
                          variant="standard"
                          style={{ width: "100%" }}
                          value={formFieldsData.taxReference}
                          onChange={(e) =>
                            handleFieldDataChange("taxReference", e.target.value as string)
                          }
                        >
                          {salesTaxes?.map((taxOption) => (
                            <MenuItem key={taxOption.id} value={taxOption.id}>
                              {taxOption.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {!hideItemsSection && (
        <TableContainer style={{ marginTop: "20px" }}>
          <Table aria-label="simple table">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow sx={{ backgroundColor: "grey.300" }}>
                <TableCell sx={{ width: "40%" }}>Item</TableCell>
                <TableCell sx={{ width: "40%" }}>Item Description</TableCell>
                <TableCell sx={{ width: "20%" }}>Item Amount</TableCell>
                <TableCell sx={{ width: "5%" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formFieldsData.items.map((item, index) => (
                <StyledTableRow
                  key={index}
                  className={index % 2 === 0 ? "outer even" : "outer odd"}
                >
                  <TableCell>
                    <FormField
                      placeholder={formFieldsData?.items[index]?.itemsName}
                      onChange={(e) =>
                        handleFieldDataChange(`items.itemsName`, e.target.value, index)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      placeholder={formFieldsData?.items[index]?.itemsDescription}
                      onChange={(e) => {
                        handleFieldDataChange(
                          "items.itemsDescription",
                          e.target.value as string,
                          index
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      align="right"
                      placeholder={formatCentsToUSD(formFieldsData?.items[index]?.itemAmount) || ""}
                      onChange={(e) =>
                        handleFieldDataChange(`items.itemAmount`, e.target.value, index)
                      }
                    />
                  </TableCell>
                  <IconButton onClick={() => handleRemove(index)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </StyledTableRow>
              ))}

              {isInvoice && (
                <StyledTableRow
                  className={job?.jobPhases.length % 2 === 0 ? "outer odd" : "outer even"}
                >
                  <TableCell>
                    <Typography variant="body1">Tax</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1"></Typography>
                  </TableCell>
                  <TableCell>
                    <FormField
                      align="right"
                      placeholder={formatCentsToUSD(formFieldsData.totalAmountTaxes) || "$0.00"}
                      onChange={(e) => handleFieldDataChange("totalAmountTaxes", e.target.value)}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "5%" }}></TableCell>
                </StyledTableRow>
              )}

              <StyledTableRow
                className={
                  isInvoice
                    ? job?.jobPhases.length % 2 !== 0
                      ? "outer odd"
                      : "outer even"
                    : job?.jobPhases.length % 2 === 0
                    ? "outer even"
                    : "outer odd"
                }
              >
                <TableCell>
                  <Typography variant="body1">Total</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1"></Typography>
                </TableCell>
                <TableCell>
                  <FormField
                    align="right"
                    placeholder={
                      formatCentsToUSD(
                        isInvoice
                          ? formFieldsData.totalAmountAfterTaxes
                          : formFieldsData.totalAmountItems
                      ) || ""
                    }
                    onChange={(e) => {
                      handleFieldDataChange(
                        isInvoice ? "totalAmountAfterTaxes" : "totalAmountItems",
                        e.target.value
                      );
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: "5%" }}></TableCell>
              </StyledTableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
